import { api } from './api';
import { API_BASE } from './apiBase';

export type VorgangStatus =
  | 'gestellt' | 'bewilligt' | 'abgelehnt' | 'beratung' | 'bericht' | 'abgeschlossen';

export interface UploadedFileDto {
  id: number;
  originalName: string;
  size: number;
  mimeType: string;
  category: string | null;
  createdAt: string;
  /** Auf einer „… Vollständig unterschrieben"-Datei gesetzt → Gegenzeichnung ist rückgängig machbar. */
  replacesFileId?: number | null;
  /** Nur Berichte: WER hat unterschrieben — 'kunde', 'berater', 'beide' oder null. */
  signatureState?: 'kunde' | 'berater' | 'beide' | null;
  /** True, wenn die automatische Klassifizierung unsicher war → „Zur Prüfung". */
  needsReview?: boolean;
  /** Grund-Code für needsReview (z.B. 'kein-original-vn', 'fremder-inhaber') — treibt den
   *  konkreten Tooltip, siehe needsReviewReasonLabel() in DocumentGrid.tsx. */
  needsReviewReason?: string | null;
}

export interface ReportRef { id: number; berichtstitel: string; status: string; startDate?: string | null; endDate?: string | null; }

export interface Vorgang {
  id: number;
  customerId: number;
  vorgangsnummer: string | null;
  status: VorgangStatus;
  bezeichnung: string | null;
  reports?: ReportRef[];
  files?: UploadedFileDto[];
  createdAt: string;
  /** Aus Aufgaben/Nachforderungen ausgeblendet — Daten bleiben erhalten. */
  archived?: boolean;
}

export interface VorgangInput {
  vorgangsnummer?: string;
  status?: VorgangStatus;
  bezeichnung?: string;
  archived?: boolean;
}

export const listVorgaenge = (customerId: number) =>
  api.get<Vorgang[]>(`/api/customers/${customerId}/vorgaenge`);

export const getVorgang = (vorgangId: number) => api.get<Vorgang>(`/api/vorgaenge/${vorgangId}`);

export const refreshCustomerBafa = (customerId: number) =>
  api.post<{ id: number; status: string }[]>(`/api/reports/refresh-bafa/customer/${customerId}`, {});

export const createVorgang = (customerId: number, input: VorgangInput) =>
  api.post<Vorgang>(`/api/customers/${customerId}/vorgaenge`, input);

export const updateVorgang = (vorgangId: number, input: VorgangInput) =>
  api.patch<Vorgang>(`/api/vorgaenge/${vorgangId}`, input);

export const deleteVorgang = (vorgangId: number) =>
  api.delete<void>(`/api/vorgaenge/${vorgangId}`);

// ── Dateien je Ebene ──
type Level = { customerId: number } | { vorgangId: number } | { reportId: number };

function levelPath(level: Level): string {
  if ('customerId' in level) return `/api/customers/${level.customerId}/files`;
  if ('vorgangId' in level) return `/api/vorgaenge/${level.vorgangId}/files`;
  return `/api/reports/${(level as { reportId: number }).reportId}/files`;
}

export const listFiles = (level: Level) => api.get<UploadedFileDto[]>(levelPath(level));

export async function uploadFile(level: Level, file: File, category?: string, rename?: boolean, signal?: AbortSignal): Promise<UploadedFileDto> {
  const form = new FormData();
  form.append('file', file);
  if (category) form.append('category', category);
  if (rename) form.append('rename', '1');
  const token = localStorage.getItem('auth_token');
  const res = await fetch(`${API_BASE}${levelPath(level)}`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
    signal,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({} as any));
    throw new Error((body && (body.message as string)) || 'Upload fehlgeschlagen');
  }
  return res.json();
}

/** Wartet in diesem Vorgang ein zur Unterschrift verschickter Bericht auf den Rücklauf?
 *  `versendetAm` = Zeitpunkt der Einladungs-Mail, sonst null. */
export const getBerichtVersand = (vorgangId: number) =>
  api.get<{ versendetAm: string | null }>(`/api/vorgaenge/${vorgangId}/bericht-versand`);

/** Lädt den unterschriebenen VN hoch und führt ihn synchron ins Original-VN zurück (grün).
 *  Ergebnis `{ merged, reason }`: bei `merged=false` bleibt die Datei zur Prüfung liegen. */
export async function uploadSignedVn(vorgangId: number, file: File, signal?: AbortSignal): Promise<{ merged: boolean; reason: string }> {
  const form = new FormData();
  form.append('file', file);
  const token = localStorage.getItem('auth_token');
  const res = await fetch(`${API_BASE}/api/vorgaenge/${vorgangId}/vn-signed-return`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
    signal,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({} as any));
    throw new Error((body && (body.message as string)) || 'Upload fehlgeschlagen');
  }
  return res.json();
}

/** Berater-Gegenzeichnung: lädt die unterschriebene Version hoch → Backend mergt + ersetzt
 *  die Quell-Karte (versteckt, wiederherstellbar). Liefert die grüne „Vollständig"-Datei. */
export async function countersignFile(sourceFileId: number, file: File, signal?: AbortSignal): Promise<UploadedFileDto> {
  const form = new FormData();
  form.append('file', file);
  const token = localStorage.getItem('auth_token');
  const res = await fetch(`${API_BASE}/api/files/${sourceFileId}/countersign`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
    signal,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({} as any));
    throw new Error((body && (body.message as string)) || 'Upload fehlgeschlagen');
  }
  return res.json();
}

/** Macht eine Gegenzeichnung rückgängig (Quell-Karte wiederherstellen, grüne verstecken). */
export const undoCountersign = (greenFileId: number) =>
  api.post<{ ok: boolean }>(`/api/files/${greenFileId}/undo-countersign`, {});

export const deleteFile = (fileId: number) => api.delete<void>(`/api/files/${fileId}`);
export const restoreFile = (fileId: number) => api.patch<UploadedFileDto>(`/api/files/${fileId}/restore`, {});
/** Speichert die manuelle Reihenfolge (Drag&Drop) der Dateien — Reihenfolge der IDs = Anzeigereihenfolge. */
export const reorderFiles = (ids: number[]) => api.patch<void>(`/api/files/reorder`, { ids });
export const setFileSigned = (fileId: number, signed: boolean) =>
  api.patch<UploadedFileDto>(`/api/files/${fileId}/signed`, { signed });
/** Ändert den Anzeigenamen (originalName) eines Dokuments. */
export const renameFile = (fileId: number, name: string) =>
  api.patch<UploadedFileDto>(`/api/files/${fileId}/rename`, { name });
export const reclassifyFile = (fileId: number) =>
  api.post<UploadedFileDto>(`/api/files/${fileId}/reclassify`, {});
/** Setzt die Kategorie eines Dokuments manuell (löscht serverseitig das „zur Prüfung"/needsReview-Flag). */
export const setFileCategory = (fileId: number, category: string) =>
  api.patch<UploadedFileDto>(`/api/files/${fileId}/category`, { category });
export const fileDownloadUrl = (fileId: number) => `${API_BASE}/api/files/${fileId}/download`;
/** Erzeugt ein Dauer-Dokument (Vollmacht/GRC/De-Minimis) für den Kunden neu — z.B. jährliche
 *  Erneuerung. Ein bestehender unsignierter Entwurf wird ersetzt, eine bereits signierte
 *  Fassung bleibt stehen, bis der neue Entwurf seinerseits unterschrieben wird. */
export const regenerateDauerdokument = (customerId: number, typ: 'vollmacht' | 'grc' | 'de_minimis') =>
  api.post<UploadedFileDto>(`/api/customers/${customerId}/bafa-forms/${typ}`, {});

/** Lädt die Datei (mit Bearer-Token) als ArrayBuffer — für clientseitiges PDF-Rendering. */
export async function fetchFileBytes(fileId: number): Promise<ArrayBuffer> {
  const token = localStorage.getItem('auth_token');
  const res = await fetch(`${API_BASE}/api/files/${fileId}/download`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error('Datei konnte nicht geladen werden');
  return res.arrayBuffer();
}

/** Seiten-Editor: baut die Zieldatei aus einer Seitenfolge neu zusammen (ersetzt, rückgängig via undo). */
export async function reassembleFile(
  targetId: number,
  pages: Array<{ fileId?: number; upload?: boolean; page: number }>,
  upload?: File | null,
): Promise<UploadedFileDto> {
  const form = new FormData();
  form.append('pages', JSON.stringify(pages));
  if (upload) form.append('upload', upload);
  const token = localStorage.getItem('auth_token');
  const res = await fetch(`${API_BASE}/api/files/${targetId}/reassemble`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({} as any));
    throw new Error((body && (body.message as string)) || 'Zusammenbauen fehlgeschlagen');
  }
  return res.json();
}

/** Lädt die Datei (mit Bearer-Token) als Blob und liefert eine Object-URL für
 *  die Inline-Vorschau. Caller muss URL.revokeObjectURL nach Gebrauch aufrufen. */
export async function fetchFileBlob(fileId: number): Promise<{ url: string; type: string }> {
  const token = localStorage.getItem('auth_token');
  const res = await fetch(`${API_BASE}/api/files/${fileId}/download`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error('Vorschau konnte nicht geladen werden');
  const blob = await res.blob();
  return { url: URL.createObjectURL(blob), type: blob.type };
}

/** Lädt alle PDF-Dokumente eines Vorgangs als ZIP (Server benennt sie BAFA_<vorgangsnummer>.zip). */
export async function downloadVorgangZip(vorgangId: number, fallbackName?: string): Promise<void> {
  const token = localStorage.getItem('auth_token');
  const res = await fetch(`${API_BASE}/api/vorgaenge/${vorgangId}/documents.zip`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    let msg = 'Download fehlgeschlagen';
    try { msg = (await res.json()).message || msg; } catch { /* kein JSON-Body */ }
    throw new Error(msg);
  }
  const blob = await res.blob();
  const cd = res.headers.get('content-disposition') || '';
  const m = cd.match(/filename="?([^"]+)"?/i);
  const filename = (m && m[1]) || fallbackName || `BAFA_${vorgangId}.zip`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

// ── Verwendungsnachweis ──

export interface VnRequestData {
  vorgangsnummer: string | null;
  plz: string | null;
  email: string | null;
  /** true, wenn die E-Mail aus dem am Vorgang gemerkten BAFA-Feld stammt (nicht aus den Stammdaten). */
  emailFromBafaFeld?: boolean;
}

/** Werte, mit denen sich der Server bei der BAFA anmeldet — für den Prüf-Dialog. */
export const getVnRequestData = (vorgangId: number) =>
  api.get<VnRequestData>(`/api/vorgaenge/${vorgangId}/vn/request-data`);

/** `override` gilt nur für DIESE Anfrage; Kunden-/Vorgangsdaten werden nicht geändert.
 *  `notifyCustomer` löst bei Erfolg eine Hinweis-Mail an den Kunden aus (nicht anklicken,
 *  sondern die BAFA-Mail an uns weiterleiten) — nötig, wenn die hinterlegte Adresse keine
 *  vom Berater überwachte Mailbox ist. */
export const requestVnLink = (
  vorgangId: number,
  override?: Partial<VnRequestData> & { notifyCustomer?: boolean },
) => api.post<void>(`/api/vorgaenge/${vorgangId}/vn/request`, override ?? {});

export const getVnStatus = (vorgangId: number) =>
  api.get<{ status: 'offen' | 'wartet' | 'bereit'; linkValidUntil: string | null }>(`/api/vorgaenge/${vorgangId}/vn`);

export const openVnLink = (vorgangId: number) =>
  api.post<{ ottLink: string }>(`/api/vorgaenge/${vorgangId}/vn/open`, {});

export interface VnFormData {
  vorgangsnummer: string | null;
  berichtId: number | null;
  berichtTitel: string | null;
  steuernummer: string | null;
  plz: string | null;
  bundesland: string | null;
  dokumente: Array<{ id: number; name: string }>;
  beratung: {
    beginn: string | null;
    ende: string | null;
    tage: number | null;
    tageListe: string[];
    bereich: string | null;
    inhaltZiel: string | null;
  };
  kosten: { netto: string | null; mitUst: string | null; mwstAktiv: boolean };
  bank: { iban: string | null };
  unternehmen: Record<string, string | null | undefined>;
  natuerlichePerson: boolean;
  verknuepfteVN: string[];
  deminimis: Array<{ datum: string | null; stelle: string | null; aktenzeichen: string | null; betrag: string | null }>;
  esfFileId: number | null;
  esfSelections: string[] | null;
}

export const getVnFormData = (vorgangId: number) =>
  api.get<VnFormData>(`/api/vorgaenge/${vorgangId}/vn-formdaten`);

export const sendVnForSigning = (
  reportId: number,
  customerEmail: string,
  overrides?: { subject?: string; message?: string; anrede?: string },
) => api.post<void>(`/api/reports/${reportId}/vn-signing-request`, { customerEmail, ...overrides });

/** `email` gilt nur für DIESEN Versand (wird nicht beim Kunden gespeichert);
 *  `force` übergeht die Sperre gegen doppeltes Einreichen. */
export const submitVnToBafa = (vorgangId: number, opts?: { email?: string; force?: boolean }) =>
  api.post<{ ok: boolean; reason: string }>(`/api/vorgaenge/${vorgangId}/vn/submit-bafa`, opts ?? {});

export interface BafaEinreichStand {
  /** Zeitpunkt des eigenen Uploads — noch ohne Beleg der BAFA. */
  gesendetAm: string | null;
  /** Eingangsbestätigung der BAFA eingetroffen → harte Sperre. */
  bestaetigtAm: string | null;
  /** Vorbelegung des Adressfelds; wird beim Senden NICHT gespeichert. */
  verteilerEmail: string | null;
}

export const getBafaEinreichStand = (vorgangId: number) =>
  api.get<BafaEinreichStand>(`/api/vorgaenge/${vorgangId}/vn/bafa-status`);

// ── Vorbereiten → Test-Link → Senden ──
export interface PreparedSigning { token: string; signingUrl: string; ready: boolean }

export const prepareSigningReport = (
  reportId: number,
  body: { customerEmail: string; forceTypes?: string[]; excludeTypes?: string[]; bitteKontoauszug?: boolean },
) => api.post<PreparedSigning>(`/api/reports/${reportId}/signing-prepare`, body);

export const prepareSigningVn = (reportId: number, customerEmail: string) =>
  api.post<PreparedSigning>(`/api/reports/${reportId}/vn-signing-prepare`, { customerEmail });

export const prepareSigningDauer = (customerId: number, customerEmail: string, types?: string[]) =>
  api.post<PreparedSigning>(`/api/customers/${customerId}/dauer-signing-prepare`, { customerEmail, types });

/** Welche Dauer-Dokumente (Vollmacht/GRC/De-Minimis) stehen gerade beim Kunden zur
 *  Auswahl — für die Häkchen-Liste im Versand-Dialog (z.B. "nur dieses eine senden"). */
export const previewDauerSigning = (customerId: number) =>
  api.get<{ send: string[]; skipped: string[]; files: { id: number; name: string; type: string; label: string }[]; hasKontoauszug: boolean }>(
    `/api/customers/${customerId}/dauer-signing-preview`,
  );

export const sendPreparedSigning = (
  token: string,
  body: { customerEmail?: string; subject?: string; message?: string; anrede?: string; closing?: string; kontoauszug?: string; cc?: string },
) => api.post<{ ok: boolean }>(`/api/signing-prepared/${token}/send`, body);

export const discardPreparedSigning = (token: string) =>
  api.post<{ ok: boolean }>(`/api/signing-prepared/${token}/discard`, {});

/** Test-Link-Status (öffentlicher Endpunkt): ready + prepError fürs Polling. */
export const getSigningStatus = (token: string) =>
  api.get<{ ready: boolean; prepError: string | null; documents: { label: string }[] }>(`/api/signing/${token}`);
